import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { UtilityContext } from "../../../contexts/UtilityContext";
import { PresetMessagesContext } from "../../../contexts/PresetMessagesContext";
import { notyf } from "../../../contexts/NotyfContext";
import MessageFields from "./MessageFields";
import {
  getLocationPreview,
  startRecertification,
} from "../../../services/recertification";

const emptyState = () => ({
  masbiaLocationId: "",
  endDate: null,
  batchSize: 2000,
  isTest: false,
  testFamilies: "",
});

/**
 * Starting a drive takes effect the moment it is submitted, so this screen shows exactly how many
 * families are about to be deactivated before the button is pressed.
 */
const RecertificationStart = forwardRef(({ onStarted }, ref) => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(emptyState());
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [excluded, setExcluded] = useState([]);
  const [message, setMessage] = useState({});
  const [audioFile, setAudioFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { dropdownValues, initDropdownValues } = useContext(UtilityContext);
  const { createOrUpdateMessage } = useContext(PresetMessagesContext);

  // Locations live in the shared dropdown values, which each page loads for itself.
  useEffect(() => {
    if (!dropdownValues.inited) {
      initDropdownValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownValues.inited]);

  useImperativeHandle(
    ref,
    () => ({
      open() {
        setForm(emptyState());
        setPreview(null);
        setExcluded([]);
        setMessage({});
        setAudioFile(null);
        setShow(true);
      },
    }),
    []
  );

  const handleClose = () => setShow(false);

  const locations = dropdownValues.masbiahLocationId || [];

  const handleLocationChange = (event) => {
    const locationId = event.target.value;
    setForm({ ...form, masbiaLocationId: locationId });
    setPreview(null);
    setExcluded([]);
    if (!locationId) return;

    setLoadingPreview(true);
    getLocationPreview(locationId)
      .then(setPreview)
      .catch(() => {})
      .finally(() => setLoadingPreview(false));
  };

  const toggleException = (name) =>
    setExcluded((current) =>
      current.includes(name)
        ? current.filter((x) => x !== name)
        : [...current, name]
    );

  const excludedCount = (preview?.exceptions || [])
    .filter((x) => excluded.includes(x.exception))
    .reduce((sum, x) => sum + x.families, 0);

  const inScope = preview ? preview.totalFamilies - excludedCount : 0;

  const activeExcluded = (preview?.exceptions || [])
    .filter((x) => excluded.includes(x.exception))
    .reduce((sum, x) => sum + x.activeFamilies, 0);

  const willDeactivate = preview ? preview.activeFamilies - activeExcluded : 0;

  const canSubmit =
    !submitting &&
    form.masbiaLocationId &&
    form.endDate &&
    (form.isTest ? form.testFamilies.trim().length > 0 : inScope > 0) &&
    (message.message || message.say || message.playFileUrl || audioFile);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const locationName =
        locations.find((l) => String(l.value) === String(form.masbiaLocationId))
          ?.title || "Location";

      // Save the message through the normal messages screen, so it lives alongside the rest and
      // can be reused or corrected later.
      const saved = await createOrUpdateMessage(
        {
          ...message,
          name: `Recertification ${locationName} ${new Date().toLocaleDateString()}`.slice(
            0,
            100
          ),
        },
        audioFile
      );

      if (!saved?.id) {
        throw new Error("The message could not be saved");
      }

      const cycle = await startRecertification({
        masbiaLocationId: Number(form.masbiaLocationId),
        endDate: form.endDate,
        excludedExceptions: form.isTest ? [] : excluded,
        inviteMessageId: saved.id,
        batchSize: Number(form.batchSize),
        isTest: form.isTest,
        testFamilies: form.testFamilies,
      });

      notyf.success(
        `Recertification started. ${cycle.progress.familiesInScope} families are in it.`
      );
      handleClose();
      onStarted(cycle);
    } catch (err) {
      if (err?.message) notyf.error(err.message);
    }
    setSubmitting(false);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Start Recertification</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id="start-recertification-form" onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Location</Form.Label>
            <Form.Select
              required
              value={form.masbiaLocationId}
              onChange={handleLocationChange}
            >
              <option value="">Choose a location</option>
              {locations.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.title}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {loadingPreview && (
            <div className="mb-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading families&hellip;
            </div>
          )}

          {preview?.openCycleId && (
            <Alert variant="warning">
              <strong>{preview.openCycleName}</strong> is still running for this
              location. Close it before starting another one.
            </Alert>
          )}

          {preview && !form.isTest && (
            <>
              <p className="mb-2">
                <strong>{preview.totalFamilies.toLocaleString()}</strong>{" "}
                families at {preview.locationName},{" "}
                <strong>{preview.activeFamilies.toLocaleString()}</strong>{" "}
                currently active.
              </p>

              {preview.exceptions.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Hold back these exceptions</Form.Label>
                <Form.Text className="d-block mb-2" muted>
                  Anyone you tick here is left alone completely &mdash; not
                  deactivated, not messaged.
                </Form.Text>
                <div
                  style={{
                    maxHeight: "220px",
                    overflowY: "auto",
                    border: "1px solid var(--bs-border-color, #dee2e6)",
                    borderRadius: "0.25rem",
                  }}
                >
                  <Table size="sm" className="mb-0">
                    <tbody>
                      {preview.exceptions.map((item) => (
                        <tr key={item.exception}>
                          <td style={{ width: "2.5rem" }}>
                            <Form.Check
                              id={`exc-${item.exception}`}
                              checked={excluded.includes(item.exception)}
                              onChange={() => toggleException(item.exception)}
                            />
                          </td>
                          <td>{item.exception}</td>
                          <td className="text-end text-muted">
                            {item.families.toLocaleString()} families,{" "}
                            {item.activeFamilies.toLocaleString()} active
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Form.Group>
              )}

              <Alert variant={willDeactivate > 0 ? "danger" : "secondary"}>
                This will deactivate{" "}
                <strong>{willDeactivate.toLocaleString()}</strong> active
                families right now, and put{" "}
                <strong>{inScope.toLocaleString()}</strong> families into the
                drive.
              </Alert>
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="recert-test-mode"
              label="Test run — pick specific families instead of the whole location"
              checked={form.isTest}
              onChange={(e) => setForm({ ...form, isTest: e.target.checked })}
            />
          </Form.Group>

          {form.isTest && (
            <Form.Group className="mb-3">
              <Form.Label>Families to test with</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Family ids or phone numbers, separated by commas"
                value={form.testFamilies}
                onChange={(e) =>
                  setForm({ ...form, testFamilies: e.target.value })
                }
              />
              <Form.Text muted>
                Only these families are deactivated and messaged. Everyone else
                at the location is untouched.
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Families can recertify until</Form.Label>
            <DatePicker
              required
              className="form-control"
              placeholderText="Pick the last day"
              selected={form.endDate}
              minDate={new Date()}
              onChange={(value) => setForm({ ...form, endDate: value })}
            />
            <Form.Text muted>
              The drive starts as soon as you press the button. This is the last
              day families can call in.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Send this many families per batch</Form.Label>
            <Form.Control
              type="number"
              min={1}
              step={100}
              style={{ maxWidth: "12rem" }}
              value={form.batchSize}
              onChange={(e) => setForm({ ...form, batchSize: e.target.value })}
            />
            <Form.Text muted>
              Nothing is sent when you press start. You send each batch yourself
              from the next screen, so the phone system isn&rsquo;t flooded.
            </Form.Text>
          </Form.Group>

          <hr />
          <h5 className="mb-3">The message families get</h5>
          <MessageFields
            idPrefix="recert-start"
            value={message}
            onChange={setMessage}
            audioFile={audioFile}
            onAudioChange={setAudioFile}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="start-recertification-form"
            disabled={!canSubmit || !!preview?.openCycleId}
          >
            {submitting && (
              <Spinner animation="border" size="sm" className="me-2" />
            )}
            {form.isTest
              ? "Start test run"
              : `Deactivate ${willDeactivate.toLocaleString()} families and start`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

export default RecertificationStart;
